/**
 * Time helpers used by the dashboard.
 *
 * The server sends `current_time` as HH:mm:ss.SSS (NTP, taken on the
 * collector side). We use it as an absolute wall-clock anchor and interpolate
 * offsets locally using the browser's monotonic clock (Date.now()).
 */

const pad = (n: number): string => String(n).padStart(2, '0');

/** Clamp a number to >= 0. */
export function clampNonNeg(n: number): number {
  return n < 0 ? 0 : n;
}

/** Format seconds as HH:mm:ss — always 3 segments, standard TV format. */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

/**
 * Format a Date as HH:mm:ss in the given IANA timezone (e.g. "Europe/Paris",
 * "Pacific/Noumea"). The timezone is per-channel, set in admin — we never use
 * the browser's locale, which would render the wrong wall-clock on a
 * misconfigured device.
 *
 * The dashboard re-renders the clock at 10 FPS, so we cache one formatter per
 * IANA tz to avoid ~30 `Intl.DateTimeFormat` allocations per second per
 * dashboard. The cache is bounded by the curated `ALLOWED_TIMEZONES` set
 * (~30 entries) so it cannot grow unbounded.
 */
const formatterByTz = new Map<string, Intl.DateTimeFormat>();

export function formatClock(d: Date, timeZone: string): string {
  let formatter = formatterByTz.get(timeZone);
  if (!formatter) {
    // `en-GB` reliably yields HH:mm:ss with hour12=false across browsers.
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    formatterByTz.set(timeZone, formatter);
  }
  return formatter.format(d);
}

/**
 * Parse the HH:mm:ss.SSS server string against a reference `now` to produce a
 * real Date.
 *
 * IMPORTANT: the collector emits `current_time` using UTC hours (see
 * FailoverManager.formatTime — it calls getUTCHours). We therefore interpret
 * the HH:mm:ss values as UTC. The resulting Date is an absolute point in time;
 * `formatClock()` later displays it in the browser's local timezone so the
 * régie user sees their own wall-clock.
 */
export function parseServerTimeOfDay(timeStr: string, referenceDate: Date): Date {
  const match = /^(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/.exec(timeStr);
  if (!match) return new Date(referenceDate.getTime());
  const [, hh, mm, ss, ms] = match;
  const d = new Date(referenceDate.getTime());
  d.setUTCHours(Number(hh), Number(mm), Number(ss), Number(ms ?? 0));

  // If the server's UTC time-of-day lands on the "wrong" calendar day relative
  // to referenceDate (edge case: dashboard ticks over midnight UTC), correct
  // the date by ±1 day based on which is closer.
  const diff = d.getTime() - referenceDate.getTime();
  if (diff > 12 * 3_600_000) d.setUTCDate(d.getUTCDate() - 1);
  else if (diff < -12 * 3_600_000) d.setUTCDate(d.getUTCDate() + 1);

  return d;
}
